import React, { useState } from 'react';
import { X, Sparkles, Loader } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function AISuggestModal({ onClose, onSelectSuggestion, existingPromptsCount = 0 }) {
  const [context, setContext] = useState('');
  const [functionTag, setFunctionTag] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!context.trim()) {
      toast.error('Please describe what you need');
      return;
    }

    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.post(
        `${API_URL}/api/ai/suggest-prompt`,
        {
          context: context,
          function_tag: functionTag || null,
          existing_prompts_count: existingPromptsCount
        },
        { withCredentials: true, headers }
      );
      setSuggestions(data.suggestions);
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast.error('Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ai-suggest-modal">
      <div className="bg-white rounded-xl p-6 w-[360px] max-h-[560px] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-[#E27D60]" />
            <h2 className="text-xl tracking-tight font-semibold text-[#1C1917]">AI Suggestions</h2>
          </div>
          <button onClick={onClose} className="text-[#78716C] hover:text-[#1C1917]" data-testid="close-modal-button">
            <X size={20} />
          </button>
        </div>

        {suggestions.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-[#57534E]">Describe what you need help with, and AI will suggest tailored prompts.</p>
            
            <div>
              <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">What do you need?</label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] placeholder-[#78716C] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
                rows="3"
                placeholder="e.g., I need help analyzing customer feedback data and creating actionable insights"
                data-testid="context-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-wider uppercase text-[#78716C] mb-1 block">Function (Optional)</label>
              <select
                value={functionTag}
                onChange={(e) => setFunctionTag(e.target.value)}
                className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-sm text-[#1C1917] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
                data-testid="function-select"
              >
                <option value="">General</option>
                <option value="Sales">Sales</option>
                <option value="Marketing">Marketing</option>
                <option value="Customer Success">Customer Success</option>
                <option value="Engineering">Engineering</option>
                <option value="Product">Product</option>
              </select>
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || !context.trim()}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-[#E7E5E4] focus:outline-none bg-[#E27D60] text-white hover:bg-[#D36C50] w-full disabled:opacity-50"
              data-testid="generate-button"
            >
              {loading ? (
                <>
                  <Loader size={16} className="mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={16} className="mr-2" />
                  Generate Suggestions
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-[#57534E] mb-3">Here are AI-generated prompt suggestions:</p>
            
            {suggestions.map((suggestion, idx) => (
              <div
                key={idx}
                className="bg-[#FAFAF9] rounded-lg border border-[#E7E5E4] p-3"
                data-testid={`suggestion-${idx}`}
              >
                <h3 className="text-sm font-medium text-[#1C1917] mb-2">{suggestion.title}</h3>
                <p className="text-xs text-[#57534E] mb-2 line-clamp-3">{suggestion.content}</p>
                {suggestion.commentary && (
                  <p className="text-xs text-[#78716C] italic mb-2">{suggestion.commentary}</p>
                )}
                <button
                  onClick={() => onSelectSuggestion(suggestion)}
                  className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-[#2C4C3B] text-white hover:bg-[#243D30] w-full"
                  data-testid="use-suggestion-button"
                >
                  Use This Prompt
                </button>
              </div>
            ))}

            <button
              onClick={() => setSuggestions([])}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] w-full"
              data-testid="try-again-button"
            >
              Try Different Context
            </button>
          </div>
        )}
      </div>
    </div>
  );
}